/* eslint-disable react-hooks/exhaustive-deps */
import * as React from "react";
import { useMemo } from "react";

import { AvailablePaymentCurrency, Locales, TicketingFormCategory, Nullable, FormType } from "@simplyk/common";
import { Control, FieldErrors, FieldValues, Path, UseFormRegister, UseFormSetValue, useWatch } from "react-hook-form";

// eslint-disable-next-line zeffy-linter-frontend/no-cross-layer-imports -- legacy cross-layer import (pre-rule); migrate when touched
import { useFormV2Context } from "@/canvas/features/FormV2/contexts/FormV2Context";
import { AmplitudeEvents } from "@/common/constants/amplitude";
import { areNonAddOnRatesSoldOut } from "@/common/helpers/ticketingOccurrence";
import { FullRateError, TicketPurchase } from "@/common/types/ticketing";
import { RateOutput, TicketingFrontendPageOutput, OccurrenceWithRatesOutput } from "@/common/types/trpc";

export interface SelectTicketInputProps<T extends FieldValues> {
  rate: RateOutput;
  ticketing: TicketingFrontendPageOutput | null;
  control: Control<T>;
  defaultValue?: number;
  setValue: UseFormSetValue<T>;
  register: UseFormRegister<T>;
  ticketsPurchasedNamePrefix: Path<T>;
  errors: FieldErrors<TicketPurchase[]> | undefined;
  index: number;
  fullRatesReturnByOrder: FullRateError[];
  remainingTicketingSeats: number;
  locale: Locales;
  allTicketsSelectedNumber: number;
  isDropDownSelect: boolean;
  totalNumberOfRates: number;
  onTicketSelectClick?: () => Promise<void>;
  canTicketSelectClick?: (ticketAmount: number) => Promise<boolean>;
  currency: AvailablePaymentCurrency;
  isClosed: boolean;
  clickSelectAmplitudeEvent?: AmplitudeEvents;
  clickChoiceAmplitudeEvent?: AmplitudeEvents;
  formCategory?: Nullable<TicketingFormCategory>;
  needToSelectADate?: boolean;
  selectedOccurrenceWithRates: Nullable<OccurrenceWithRatesOutput>;
  disableAutoFocus?: boolean;
  titleSuffix?: Nullable<string>;
}

export const getRemainingTicketsFromTicketingLimit = (
  rate: RateOutput,
  purchasedNumber: number | undefined,
  remainingTicketingSeats: number,
  allTicketsSelectedNumber: number
) => {
  const selectedTicketsNumber = purchasedNumber || 0;
  // Checking if it's a group ticket
  if (rate.attendeesByTicket && rate.attendeesByTicket >= 1) {
    const selectedSeatsNumber = selectedTicketsNumber * rate.attendeesByTicket;
    const selectedSeatsInOtherRates = allTicketsSelectedNumber - selectedSeatsNumber;
    return Math.floor((remainingTicketingSeats - selectedSeatsInOtherRates) / rate.attendeesByTicket);
  } else {
    return remainingTicketingSeats - (allTicketsSelectedNumber - selectedTicketsNumber);
  }
};

export const getRemainingTicketsFromRateLimit = (
  rate: RateOutput,
  fullRatesReturnByOrder: FullRateError[],
  rateRemainingTickets?: Nullable<number>
) => {
  const rateErrorReturnByOrder = fullRatesReturnByOrder.find((rateError) => rate.id === rateError.id);
  const remainingRateSeats = rateRemainingTickets ?? Infinity;
  const remainingRateSeatsReturnByOrder = rateErrorReturnByOrder?.remainingTickets ?? Infinity;

  const remainingSeats = Math.min(remainingRateSeats, remainingRateSeatsReturnByOrder);
  // Checking if it's a group ticket
  return rate.attendeesByTicket && rate.attendeesByTicket >= 1
    ? Math.floor(remainingSeats / rate.attendeesByTicket)
    : remainingSeats;
};

export const useSelectTicketInput = <T extends FieldValues>({
  control,
  setValue,
  errors,
  ticketsPurchasedNamePrefix,
  rate,
  index,
  fullRatesReturnByOrder,
  remainingTicketingSeats,
  allTicketsSelectedNumber,
  locale,
  isDropDownSelect,
  totalNumberOfRates,
  onTicketSelectClick,
  canTicketSelectClick,
  formCategory,
  selectedOccurrenceWithRates,
  isClosed,
  needToSelectADate,
}: SelectTicketInputProps<T>) => {
  const { isP2PLinkedEvent } = useFormV2Context(FormType.Ticketing);
  const purchasedNumberName = `${ticketsPurchasedNamePrefix}.${index}.purchasedNumber` as Path<T>;
  const watchedTicketsPurchased = useWatch({
    control,
    name: ticketsPurchasedNamePrefix,
  }) as TicketPurchase[];
  const rateErrorName = errors?.[index];

  const remainingTicketingTickets = getRemainingTicketsFromTicketingLimit(
    rate,
    watchedTicketsPurchased?.[index]?.purchasedNumber,
    remainingTicketingSeats,
    allTicketsSelectedNumber
  );

  const currentOccurrenceWithRates = useMemo(
    () => selectedOccurrenceWithRates?.rates.find((rateItem) => rateItem.rateId === rate.id),
    [rate.id, selectedOccurrenceWithRates?.rates]
  );

  const remainingRateTickets = getRemainingTicketsFromRateLimit(
    rate,
    fullRatesReturnByOrder,
    currentOccurrenceWithRates?.remainingTickets
  );
  const remainingTickets = Math.min(remainingRateTickets, remainingTicketingTickets);

  const minimumToBuy = rate?.minimumToBuy || 0;
  const maximumToBuy = rate?.maximumToBuy ? Math.min(rate?.maximumToBuy, remainingTickets) : remainingTickets;

  const isShop = formCategory === TicketingFormCategory.Shop;
  const isMembership = formCategory === TicketingFormCategory.MembershipV2;

  const displayRemainingTickets = isShop
    ? (rate?.maximumToBuy || NaN) >= remainingRateTickets
    : remainingRateTickets >= remainingTicketingTickets &&
      (rate?.maximumToBuy || NaN) >= remainingTicketingTickets &&
      !isClosed &&
      !needToSelectADate;

  const ticketOptionNumber = maximumToBuy - Math.max(minimumToBuy, 0) + 1;

  const ticketOptions = useMemo(
    () =>
      new Array(Math.max(ticketOptionNumber, 0)).fill(undefined).map((_undefined: undefined, index) => {
        return index + minimumToBuy;
      }),
    [minimumToBuy, ticketOptionNumber]
  );

  const displayEventFull = useMemo(
    () =>
      Boolean(selectedOccurrenceWithRates?.rates.length) && areNonAddOnRatesSoldOut(selectedOccurrenceWithRates?.rates),
    [selectedOccurrenceWithRates?.rates]
  );

  const displayRateFull =
    !displayEventFull &&
    currentOccurrenceWithRates?.remainingTickets !== undefined &&
    currentOccurrenceWithRates?.remainingTickets !== null &&
    currentOccurrenceWithRates?.remainingTickets < (rate.attendeesByTicket || 1);

  const displayRatePicker = !displayRateFull && !displayEventFull && isDropDownSelect;

  const rateField = rate?.rateFields?.find((rateField) => rateField.locale === locale) || rate?.rateFields?.[0];

  const onTicketAmountClick = React.useCallback(async () => {
    if (canTicketSelectClick) {
      const canClick = await canTicketSelectClick(rate.amount);
      if (!canClick) {
        return;
      }
    }
    setValue(purchasedNumberName, 1 as never);
    // For memberships and P2P-linked events, clear other rate selections to ensure only one ticket
    if (isMembership || isP2PLinkedEvent) {
      new Array<number>(totalNumberOfRates).fill(0).forEach((_, idx) => {
        if (idx !== index) {
          setValue(`${ticketsPurchasedNamePrefix}.${idx}.purchasedNumber` as Path<T>, 0 as never);
        }
      });
    }
    if (onTicketSelectClick) {
      await onTicketSelectClick();
    }
  }, [
    canTicketSelectClick,
    setValue,
    purchasedNumberName,
    totalNumberOfRates,
    onTicketSelectClick,
    rate.amount,
    index,
    ticketsPurchasedNamePrefix,
    isMembership,
    isP2PLinkedEvent,
  ]);

  return {
    purchasedNumberName,
    watchedTicketsPurchased,
    rateErrorName,
    minimumToBuy,
    remainingTickets,
    displayRemainingTickets,
    rateField,
    displayFull: displayEventFull || displayRateFull,
    displayRatePicker,
    ticketOptions,
    onTicketAmountClick,
    isMembership,
  };
};
